import React, { Component } from 'react';
import clx from 'classnames';
import _ from './utils';
import Lifecycle from './lifecycle';
import Methods from './methods';
import defaultProps from './defaultProps';
import './styles.scss';

export const TableDefaults = defaultProps;

export default class Table extends Methods(Lifecycle(Component)) {
  static defaultProps = defaultProps;

  constructor(props) {
    super();

    this.getResolvedState = this.getResolvedState.bind(this);
    this.getDataModel = this.getDataModel.bind(this);
    this.getSortedData = this.getSortedData.bind(this);
    this.fireFetchData = this.fireFetchData.bind(this);
    this.getPropOrState = this.getPropOrState.bind(this);
    this.getStateOrProp = this.getStateOrProp.bind(this);
    this.filterData = this.filterData.bind(this);
    this.sortData = this.sortData.bind(this);
    this.getMinRows = this.getMinRows.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.onPageSizeChange = this.onPageSizeChange.bind(this);
    this.sortColumn = this.sortColumn.bind(this);
    this.filterColumn = this.filterColumn.bind(this);
    this.resizeColumnStart = this.resizeColumnStart.bind(this);
    this.resizeColumnEnd = this.resizeColumnEnd.bind(this);
    this.resizeColumnMoving = this.resizeColumnMoving.bind(this);

    this.state = {
      page: 0,
      pageSize: props.defaultPageSize,
      sorted: props.defaultSorted,
      expanded: props.defaultExpanded,
      filtered: props.defaultFiltered,
      resized: props.defaultResized,
      currentlyResizing: false,
      skipNextSort: false,
    };
  }

  render() {
    const resolvedState = this.getResolvedState();
    const {
      children,
      className,
      style,
      getProps,
      getTableProps,
      getColGroupColProps,
      getTheadGroupProps,
      getTheadGroupTrProps,
      getTheadGroupThProps,
      getTheadProps,
      getTheadTrProps,
      getTheadThProps,
      getTheadFilterProps,
      getTheadFilterTrProps,
      getTheadFilterThProps,
      getTbodyProps,
      getTrProps,
      getTdProps,
      getTfootProps,
      getTfootTrProps,
      getTfootTdProps,
      getPaginationProps,
      getLoadingProps,
      getNoDataProps,
      getResizerProps,
      showPagination,
      showPaginationTop,
      showPaginationBottom,
      showSortOrder,
      manual,
      loadingText,
      noDataText,
      sortable,
      resizable,
      filterable,
      footerPlacement,
      // Pivoting State
      pivotIDKey,
      pivotValKey,
      pivotBy,
      subRowsKey,
      aggregatedKey,
      originalKey,
      indexKey,
      rowKeyProp,
      groupedByPivotKey,
      // Fixed
      fixedColumnsCount,
      // State
      loading,
      pageSize,
      page,
      sorted,
      filtered,
      resized,
      expanded,
      pages,
      onExpandedChange,
      // Components
      TableWrapper,
      TableComponent,
      TheadComponent,
      TbodyComponent,
      TrComponent,
      ThComponent,
      TdComponent,
      TfootComponent,
      PaginationComponent,
      LoadingComponent,
      SubComponent,
      NoDataComponent,
      ResizerComponent,
      SortOrderComponent,
      ExpanderComponent,
      PivotValueComponent,
      PivotComponent,
      AggregatedComponent,
      FilterComponent,
      PadRowComponent,
      // Data model
      resolvedData,
      allVisibleColumns,
      headerGroups,
      hasHeaderGroups,
      // Sorted Data
      sortedData,
      currentlyResizing,
      width: totalWidth,
    } = resolvedState;

    // Pagination
    let pageRows = sortedData;
    let startRow = 0;
    let endRow = sortedData.length;

    if (manual) {
      pageRows = resolvedData;
    } else if (pageSize > 0) {
      startRow = pageSize * page;
      endRow = startRow + pageSize;
      pageRows = sortedData.slice(startRow, endRow);
    }

    const minRows = this.getMinRows();
    const padRows = _.range(Math.max(minRows - pageRows.length, 0));

    const hasColumnFooter = allVisibleColumns.some(d => d.Footer);
    const hasFilters = filterable || allVisibleColumns.some(d => d.filterable);

    const recurseRowsViewIndex = (rows, path = [], index = -1) => {
      return [
        rows.map((row, i) => {
          index++;
          const rowWithViewIndex = {
            ...row,
            _viewIndex: index,
          };
          const newPath = path.concat([i]);
          if (rowWithViewIndex[subRowsKey] && _.get(expanded, newPath)) {
            [rowWithViewIndex[subRowsKey], index] = recurseRowsViewIndex(
              rowWithViewIndex[subRowsKey],
              newPath,
              index
            );
          }
          return rowWithViewIndex;
        }),
        index,
      ];
    };
    [pageRows] = recurseRowsViewIndex(pageRows);

    const canPrevious = page > 0;
    const canNext = page + 1 < pages;

    let rowIndex = -1;

    const finalState = {
      ...resolvedState,
      startRow,
      endRow,
      pageRows,
      minRows,
      padRows,
      hasColumnFooter,
      canPrevious,
      canNext,
    };

    const fixedColumnsProps = [];
    let rawColumnsWidth = 0;
    let floorColumnsWidth = 0;

    // Visual Components

    const makeColGroup = () => {
      return <colgroup>{allVisibleColumns.map(makeCol)}</colgroup>;
    };

    const makeCol = (column, i) => {
      const isDummy = i === allVisibleColumns.length - 1;
      const isFixed = i < fixedColumnsCount;
      const isInnerFixed = i < fixedColumnsCount - 1;
      const isLastFixed = i === fixedColumnsCount - 1;

      const resizedCol = resized.find(x => x.id === column.id) || {};

      const colgroupColProps = _.splitProps(
        getColGroupColProps(finalState, undefined, column, this)
      );
      const columnColgroupColProps = _.splitProps(
        column.getColGroupColProps(finalState, undefined, column, this)
      );

      const classes = [
        column.colGroupColClassName,
        colgroupColProps.className,
        columnColgroupColProps.className,
      ];

      const styles = {
        ...column.colGroupColStyle,
        ...colgroupColProps.style,
        ...columnColgroupColProps.style,
      };

      let minWidth = column.minWidth;
      let maxWidth = column.maxWidth;
      let width = _.getFirstDefined(resizedCol.value, column.width, minWidth, 0);

      // calculate px
      if (totalWidth) {
        const insideWidth = totalWidth - 2; // Subtract borders
        const widthIsPercents = _.isPercents(width);
        const minWidthIsPercents = _.isPercents(minWidth);
        const maxWidthIsPercents = _.isPercents(maxWidth);

        const widthValue = parseInt(width, 10);
        const minWidthValue = minWidth && parseInt(minWidth, 10);
        const maxWidthValue = maxWidth && parseInt(maxWidth, 10);

        width = widthIsPercents ? _.percentsToPx(widthValue, insideWidth) : widthValue;

        minWidth = minWidthIsPercents ? _.percentsToPx(minWidthValue, insideWidth) : minWidthValue;

        maxWidth = maxWidthIsPercents ? _.percentsToPx(maxWidthValue, insideWidth) : maxWidthValue;

        width = _.clamp(width, minWidth, maxWidth);

        rawColumnsWidth += width;
        width = Math.floor(width);
        floorColumnsWidth += width;

        const diff = Math.floor(rawColumnsWidth - floorColumnsWidth);

        if (diff >= 1) {
          width += diff;
          floorColumnsWidth += diff;
        }

        if (isFixed) {
          const left = fixedColumnsProps.reduce((value, item) => value + item.width, 0);
          fixedColumnsProps.push({ width, left });

          if (isLastFixed) {
            width += left;
          }
        }

        width = `${width}px`;
      }

      if (isInnerFixed) {
        return null;
      }

      return (
        <col
          key={i + '-' + column.id}
          className={clx('rt-colgroup-col', classes, {
            '-dummy': isDummy,
          })}
          style={{
            ...styles,
            width: !isDummy ? width : undefined,
            minWidth: !isDummy ? minWidth : undefined,
            maxWidth: !isDummy ? maxWidth : undefined,
          }}
        />
      );
    };

    const makeHeaderGroups = () => {
      const theadGroupProps = _.splitProps(
        getTheadGroupProps(finalState, undefined, undefined, this)
      );
      const theadGroupTrProps = _.splitProps(
        getTheadGroupTrProps(finalState, undefined, undefined, this)
      );
      return (
        <TheadComponent
          className={clx('-headerGroups', theadGroupProps.className)}
          style={theadGroupProps.style}
          {...theadGroupProps.rest}
        >
          <TrComponent
            className={theadGroupTrProps.className}
            style={theadGroupTrProps.style}
            {...theadGroupTrProps.rest}
          >
            {headerGroups.map(makeHeaderGroup)}
          </TrComponent>
        </TheadComponent>
      );
    };

    const makeHeaderGroup = (column, i) => {
      const theadGroupThProps = _.splitProps(
        getTheadGroupThProps(finalState, undefined, column, this)
      );
      const columnHeaderProps = _.splitProps(
        column.getHeaderProps(finalState, undefined, column, this)
      );

      const classes = [
        column.headerClassName,
        theadGroupThProps.className,
        columnHeaderProps.className,
      ];

      const styles = {
        ...column.headerStyle,
        ...theadGroupThProps.style,
        ...columnHeaderProps.style,
      };

      const colSpan = column.columns.length;

      const rest = {
        ...theadGroupThProps.rest,
        ...columnHeaderProps.rest,
        colSpan,
      };

      const isDummy = i === headerGroups.length - 1;
      const isFixed = i < fixedColumnsCount && totalWidth;

      if (isFixed) {
        styles.width = fixedColumnsProps[i].width;
        styles.left = fixedColumnsProps[i].left;
      }

      const content = _.normalizeComponent(column.Header, {
        data: sortedData,
        column: column,
      });

      return (
        <ThComponent
          key={i + '-' + column.id}
          className={clx(classes, {
            '-dummy': isDummy,
            '-empty': !content,
            '-fixed': isFixed,
          })}
          style={styles}
          {...rest}
        >
          {content}
        </ThComponent>
      );
    };

    const makeHeaders = () => {
      const theadProps = _.splitProps(getTheadProps(finalState, undefined, undefined, this));
      const theadTrProps = _.splitProps(getTheadTrProps(finalState, undefined, undefined, this));
      return (
        <TheadComponent
          className={clx('-header', theadProps.className)}
          style={theadProps.style}
          {...theadProps.rest}
        >
          <TrComponent
            className={theadTrProps.className}
            style={theadTrProps.style}
            {...theadTrProps.rest}
          >
            {allVisibleColumns.map(makeHeader)}
          </TrComponent>
        </TheadComponent>
      );
    };

    const makeFilter = column => {
      const isFilterable = _.getFirstDefined(column.filterable, filterable, false);

      if (!isFilterable) {
        return null;
      }

      const filter = filtered.find(filter => filter.id === column.id);
      const ResolvedFilterComponent = column.Filter || FilterComponent;

      return _.normalizeComponent(
        ResolvedFilterComponent,
        {
          column,
          filter,
          onChange: value => this.filterColumn(column, value),
        },
        defaultProps.column.Filter
      );
    };

    const makeHeader = (column, i) => {
      const sort = sorted.find(d => d.id === column.id);
      const show = typeof column.show === 'function' ? column.show() : column.show;
      const theadThProps = _.splitProps(getTheadThProps(finalState, undefined, column, this));
      const columnHeaderProps = _.splitProps(
        column.getHeaderProps(finalState, undefined, column, this)
      );

      const classes = [column.headerClassName, theadThProps.className, columnHeaderProps.className];

      const styles = {
        ...column.headerStyle,
        ...theadThProps.style,
        ...columnHeaderProps.style,
      };

      const rest = {
        ...theadThProps.rest,
        ...columnHeaderProps.rest,
      };

      const isResizable = _.getFirstDefined(column.resizable, resizable, false);
      const resizer = isResizable ? (
        <ResizerComponent
          onMouseDown={e => this.resizeColumnStart(column, e, false)}
          onTouchStart={e => this.resizeColumnStart(column, e, true)}
          {...resizerProps}
        />
      ) : null;

      const sortOrder = sort && showSortOrder ? <SortOrderComponent desc={sort.desc} /> : null;

      const filter = makeFilter(column);

      const isSortable = _.getFirstDefined(column.sortable, sortable, false);
      const isDummy = i === allVisibleColumns.length - 1;
      const isFixed = i < fixedColumnsCount && totalWidth;

      if (isFixed) {
        styles.width = fixedColumnsProps[i].width;
        styles.left = fixedColumnsProps[i].left;
      }

      return (
        <ThComponent
          key={i + '-' + column.id}
          className={clx(classes, 'rt-resizable-header', {
            '-cursor-pointer': isSortable,
            '-hidden': !show,
            '-dummy': isDummy,
            '-fixed': isFixed,
            'rt-header-pivot': pivotBy && pivotBy.slice(0, -1).includes(column.id),
          })}
          style={styles}
          toggleSort={e => {
            isSortable && this.sortColumn(column, e.shiftKey);
          }}
          {...rest}
        >
          <span className="rt-resizable-header-content">
            {_.normalizeComponent(column.Header, {
              data: sortedData,
              column: column,
            })}
          </span>
          {filter}
          {sortOrder}
          {resizer}
        </ThComponent>
      );
    };

    const makePageRow = (row, i, path = []) => {
      const original = row[originalKey];
      const rowKey = rowKeyProp ? original[rowKeyProp] : undefined;
      const rowInfo = {
        original,
        row: row,
        index: row[indexKey],
        viewIndex: ++rowIndex,
        level: path.length,
        nestingPath: path.concat([rowKey || i]),
        aggregated: row[aggregatedKey],
        groupedByPivot: row[groupedByPivotKey],
        subRows: row[subRowsKey],
      };
      const isExpanded = _.get(expanded, rowInfo.nestingPath);
      const trProps = _.splitProps(getTrProps(finalState, rowInfo, undefined, this));

      const rows = [
        <TrComponent
          key={rowInfo.nestingPath.join('_')}
          className={clx(trProps.className, row._viewIndex % 2 ? '-even' : '-odd')}
          style={trProps.style}
          {...trProps.rest}
        >
          {allVisibleColumns.map((column, i2) => {
            const show = typeof column.show === 'function' ? column.show() : column.show;
            const tdProps = _.splitProps(getTdProps(finalState, rowInfo, column, this));
            const columnProps = _.splitProps(column.getProps(finalState, rowInfo, column, this));

            const classes = [tdProps.className, column.className, columnProps.className];

            const styles = {
              ...tdProps.style,
              ...column.style,
              ...columnProps.style,
            };

            const isFixed = i2 < fixedColumnsCount && totalWidth;

            if (isFixed) {
              styles.width = fixedColumnsProps[i2].width;
              styles.left = fixedColumnsProps[i2].left;
            }

            const cellInfo = {
              ...rowInfo,
              isExpanded,
              column: { ...column },
              value: rowInfo.row[column.id],
              pivoted: column.pivoted,
              expander: column.expander,
              displayPivotSubRows: column.displayPivotSubRows,
              resized,
              show,
              tdProps,
              columnProps,
              classes,
              styles,
            };

            const value = cellInfo.value;

            let interactionProps;
            let isBranch;
            let isPreview;

            const onExpanderClick = e => {
              let newExpanded = _.clone(expanded);
              if (isExpanded) {
                newExpanded = _.set(newExpanded, cellInfo.nestingPath, false);
              } else {
                newExpanded = _.set(newExpanded, cellInfo.nestingPath, {});
              }

              return this.setStateWithData(
                {
                  expanded: newExpanded,
                },
                () => {
                  onExpandedChange && onExpandedChange(newExpanded, cellInfo.nestingPath, e);
                }
              );
            };

            // Default to a standard cell
            let resolvedCell = _.normalizeComponent(column.Cell, cellInfo, value);

            // Resolve Renderers
            const ResolvedAggregatedComponent =
              column.Aggregated || (!column.aggregate ? AggregatedComponent : column.Cell);
            const ResolvedExpanderComponent = column.Expander || ExpanderComponent;
            const ResolvedPivotValueComponent = column.PivotValue || PivotValueComponent;
            const DefaultResolvedPivotComponent =
              PivotComponent ||
              (props => (
                <div>
                  <ResolvedExpanderComponent {...props} />
                  <ResolvedPivotValueComponent {...props} />
                </div>
              ));
            const ResolvedPivotComponent = column.Pivot || DefaultResolvedPivotComponent;

            // Is this cell expandable?
            if (cellInfo.pivoted || cellInfo.expander) {
              // Make it expandable by defualt
              cellInfo.expandable = true;
              interactionProps = {
                onClick: onExpanderClick,
              };
              // If pivoted, has no subRows, and does not have a subComponent, do not make expandable
              if (cellInfo.pivoted) {
                if (!cellInfo.subRows) {
                  if (!SubComponent) {
                    cellInfo.expandable = false;
                    interactionProps = {};
                  }
                }
              }
            }

            if (cellInfo.pivoted) {
              // Is this column a branch?
              isBranch = rowInfo.row[pivotIDKey] === column.id && cellInfo.subRows;
              // Should this column be blank?
              isPreview =
                pivotBy.indexOf(column.id) > pivotBy.indexOf(rowInfo.row[pivotIDKey]) &&
                cellInfo.subRows;
              // Pivot Cell Render Override
              if (isBranch) {
                // isPivot
                resolvedCell = _.normalizeComponent(
                  ResolvedPivotComponent,
                  {
                    ...cellInfo,
                    value: row[pivotValKey],
                  },
                  row[pivotValKey]
                );
              } else if (isPreview) {
                // Show the pivot preview
                resolvedCell = _.normalizeComponent(ResolvedAggregatedComponent, cellInfo, value);
              } else if (!cellInfo.displayPivotSubRows) {
                resolvedCell = null;
              }
            } else if (cellInfo.aggregated) {
              resolvedCell = _.normalizeComponent(ResolvedAggregatedComponent, cellInfo, value);
            }

            if (cellInfo.expander) {
              resolvedCell = _.normalizeComponent(
                ResolvedExpanderComponent,
                cellInfo,
                row[pivotValKey]
              );
              if (pivotBy) {
                if (cellInfo.groupedByPivot) {
                  resolvedCell = null;
                }
                if (!cellInfo.subRows && !SubComponent) {
                  resolvedCell = null;
                }
              }
            }

            // Return the cell
            return (
              <TdComponent
                key={i2 + '-' + column.id}
                className={clx(
                  classes,
                  !show && '-hidden',
                  cellInfo.expandable && 'rt-expandable',
                  (isBranch || isPreview) && 'rt-pivot',
                  isFixed && '-fixed',
                  isFixed && !resolvedCell && '-empty'
                )}
                style={styles}
                {...interactionProps}
                {...tdProps.rest}
                {...columnProps.rest}
              >
                {resolvedCell}
              </TdComponent>
            );
          })}
        </TrComponent>,
      ];

      if (rowInfo.subRows && isExpanded) {
        rowInfo.subRows.forEach((d, i) => rows.push(...makePageRow(d, i, rowInfo.nestingPath)));
      } else if (SubComponent && !rowInfo.subRows && isExpanded) {
        rows.push(SubComponent(rowInfo));
      }

      return rows;
    };

    const makePadRow = (row, i) => {
      const trProps = _.splitProps(getTrProps(finalState, undefined, undefined, this));
      return (
        <TrComponent
          className={clx(
            '-padRow',
            (pageRows.length + i) % 2 ? '-even' : '-odd',
            trProps.className
          )}
          style={trProps.style || {}}
        >
          {allVisibleColumns.map(makePadColumn)}
        </TrComponent>
      );
    };

    const makePadColumn = (column, i) => {
      const show = typeof column.show === 'function' ? column.show() : column.show;
      const tdProps = _.splitProps(getTdProps(finalState, undefined, column, this));
      const columnProps = _.splitProps(column.getProps(finalState, undefined, column, this));

      const classes = [tdProps.className, column.className, columnProps.className];

      const styles = {
        ...tdProps.style,
        ...column.style,
        ...columnProps.style,
      };

      const isFixed = i < fixedColumnsCount && totalWidth;

      if (isFixed) {
        styles.width = fixedColumnsProps[i].width;
        styles.left = fixedColumnsProps[i].left;
      }

      return (
        <TdComponent
          key={i + '-' + column.id}
          className={clx(classes, !show && '-hidden', {
            '-fixed': isFixed,
          })}
          style={styles}
          {...tdProps.rest}
        >
          {_.normalizeComponent(PadRowComponent)}
        </TdComponent>
      );
    };

    const makeColumnFooters = () => {
      const tFootProps = getTfootProps(finalState, undefined, undefined, this);
      const tFootTrProps = _.splitProps(getTfootTrProps(finalState, undefined, undefined, this));
      return (
        <TfootComponent
          className={tFootProps.className}
          style={tFootProps.style}
          {...tFootProps.rest}
        >
          <TrComponent
            className={clx(tFootTrProps.className)}
            style={tFootTrProps.style}
            {...tFootTrProps.rest}
          >
            {allVisibleColumns.map(makeColumnFooter)}
          </TrComponent>
        </TfootComponent>
      );
    };

    const makeColumnFooter = (column, i) => {
      const show = typeof column.show === 'function' ? column.show() : column.show;
      const tFootTdProps = _.splitProps(getTfootTdProps(finalState, undefined, undefined, this));
      const columnProps = _.splitProps(column.getProps(finalState, undefined, column, this));
      const columnFooterProps = _.splitProps(
        column.getFooterProps(finalState, undefined, column, this)
      );

      const classes = [
        tFootTdProps.className,
        column.className,
        columnProps.className,
        columnFooterProps.className,
      ];

      const styles = {
        ...tFootTdProps.style,
        ...column.style,
        ...columnProps.style,
        ...columnFooterProps.style,
      };

      const isFixed = i < fixedColumnsCount && totalWidth;

      if (isFixed) {
        styles.width = fixedColumnsProps[i].width;
        styles.left = fixedColumnsProps[i].left;
      }

      return (
        <TdComponent
          key={i + '-' + column.id}
          className={clx(classes, !show && '-hidden', {
            '-fixed': isFixed,
          })}
          style={styles}
          {...columnProps.rest}
          {...tFootTdProps.rest}
          {...columnFooterProps.rest}
        >
          {_.normalizeComponent(column.Footer, {
            data: sortedData,
            column: column,
          })}
        </TdComponent>
      );
    };

    const makePagination = () => {
      const paginationProps = _.splitProps(
        getPaginationProps(finalState, undefined, undefined, this)
      );
      return (
        <PaginationComponent
          {...resolvedState}
          pages={pages}
          canPrevious={canPrevious}
          canNext={canNext}
          onPageChange={this.onPageChange}
          onPageSizeChange={this.onPageSizeChange}
          className={paginationProps.className}
          style={paginationProps.style}
          {...paginationProps.rest}
        />
      );
    };

    const rootProps = _.splitProps(getProps(finalState, undefined, undefined, this));
    const tableProps = _.splitProps(getTableProps(finalState, undefined, undefined, this));
    const tBodyProps = _.splitProps(getTbodyProps(finalState, undefined, undefined, this));
    const loadingProps = getLoadingProps(finalState, undefined, undefined, this);
    const noDataProps = getNoDataProps(finalState, undefined, undefined, this);
    const resizerProps = getResizerProps(finalState, undefined, undefined, this);

    const displayFooterTop = footerPlacement === 'top' || footerPlacement === 'both';
    const displayFooterBottom = footerPlacement === 'bottom' || footerPlacement === 'both';

    const makeTable = () => {
      const colGroup = makeColGroup();
      const pagination = makePagination();
      const footer = hasColumnFooter ? makeColumnFooters() : null;

      if (!pageRows.length) {
        return (
          <NoDataComponent {...noDataProps}>{_.normalizeComponent(noDataText)}</NoDataComponent>
        );
      }

      return (
        <div
          className={clx('ReactTable', className, rootProps.className)}
          style={{
            width: totalWidth,
            ...style,
            ...rootProps.style,
          }}
          {...rootProps.rest}
        >
          {showPagination && showPaginationTop ? (
            <div className="pagination-top">{pagination}</div>
          ) : null}
          <TableWrapper wrapperRef={c => (this.tableWrapper = c)}>
            <TableComponent
              className={clx(tableProps.className, currentlyResizing ? 'rt-resizing' : '')}
              style={tableProps.style}
              {...tableProps.rest}
            >
              {colGroup}
              {hasHeaderGroups ? makeHeaderGroups() : null}
              {makeHeaders()}
              {displayFooterTop && footer}
              <TbodyComponent
                className={clx(tBodyProps.className)}
                style={tBodyProps.style}
                {...tBodyProps.rest}
              >
                {pageRows.map((d, i) => makePageRow(d, i))}
                {padRows.map(makePadRow)}
              </TbodyComponent>
              {displayFooterBottom && footer}
            </TableComponent>
          </TableWrapper>
          {showPagination && showPaginationBottom ? (
            <div className="pagination-bottom">{pagination}</div>
          ) : null}
          <LoadingComponent loading={loading} loadingText={loadingText} {...loadingProps} />
        </div>
      );
    };

    // childProps are optionally passed to a function-as-a-child
    return children ? children(finalState, makeTable, this) : makeTable();
  }
}
